import React, { useState } from 'react';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface AddPropertyDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onAdd: (property: {
		name: string;
		type: 'text' | 'date' | 'checkbox' | 'number';
	}) => void;
	existingProperties?: Array<{ name: string }>;
}

const AddPropertyDialog: React.FC<AddPropertyDialogProps> = ({
	open,
	onOpenChange,
	onAdd,
	existingProperties = [],
}) => {
	const [name, setName] = useState('');
	const [type, setType] = useState<'text' | 'date' | 'checkbox' | 'number'>(
		'text'
	);
	const [error, setError] = useState('');

	const handleSubmit = () => {
		// Validate name
		if (!name.trim()) {
			setError('Property name is required');
			return;
		}

		// Check for duplicate names
		if (
			existingProperties.some(
				(p) => p.name.toLowerCase() === name.trim().toLowerCase()
			)
		) {
			setError('A property with this name already exists');
			return;
		}

		// Add the property
		onAdd({
			name: name.trim(),
			type,
		});

		// Reset and close
		setName('');
		setType('text');
		setError('');
		onOpenChange(false);
	};

	const handleClose = () => {
		setName('');
		setType('text');
		setError('');
		onOpenChange(false);
	};

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-[425px]">
				<DialogHeader>
					<DialogTitle>Add Property</DialogTitle>
					<DialogDescription>
						Add a custom property to track additional information
						for items in this pipeline.
					</DialogDescription>
				</DialogHeader>

				<div className="grid gap-4 py-4">
					<div className="grid gap-2">
						<Label htmlFor="property-name">Property Name</Label>
						<Input
							id="property-name"
							value={name}
							onChange={(e) => {
								setName(e.target.value);
								setError('');
							}}
							placeholder="e.g., Budget, Contact Email, Project Phase"
							autoFocus
						/>
					</div>

					<div className="grid gap-2">
						<Label htmlFor="property-type">Property Type</Label>
						<Select
							value={type}
							onValueChange={(value: any) => setType(value)}
						>
							<SelectTrigger id="property-type">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="text">
									<div className="flex flex-col items-start">
										<span className="font-medium">
											Text
										</span>
										<span className="text-xs text-gray-500">
											Short or long text content
										</span>
									</div>
								</SelectItem>
								<SelectItem value="number">
									<div className="flex flex-col items-start">
										<span className="font-medium">
											Number
										</span>
										<span className="text-xs text-gray-500">
											Numeric values
										</span>
									</div>
								</SelectItem>
								<SelectItem value="date">
									<div className="flex flex-col items-start">
										<span className="font-medium">
											Date
										</span>
										<span className="text-xs text-gray-500">
											Calendar date picker
										</span>
									</div>
								</SelectItem>
								<SelectItem value="checkbox">
									<div className="flex flex-col items-start">
										<span className="font-medium">
											Checkbox
										</span>
										<span className="text-xs text-gray-500">
											Yes/No or True/False values
										</span>
									</div>
								</SelectItem>
							</SelectContent>
						</Select>
					</div>

					{error && (
						<Alert variant="destructive">
							<AlertCircle className="h-4 w-4" />
							<AlertDescription>{error}</AlertDescription>
						</Alert>
					)}
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={handleClose}>
						Cancel
					</Button>
					<Button onClick={handleSubmit}>Add Property</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default AddPropertyDialog;
